<template>
  <div class="add-post">
    <h1>Edytuj użytkownika</h1>
    <el-form ref="user" :model="user" label-width="100px" class="add-post-form">
      <el-form-item
        label="Imię"
        prop="name"
        :rules="[
          { required: true, message: 'Imię jest wymagane'},
        ]"
      >
        <el-input v-model="user.name" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Email"
        prop="email"
        :rules="[
          { required: true, message: 'Email jest wymagany'},
        ]"
      >
        <el-input v-model="user.email" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Telefon"
        prop="phone"
      >
        <el-input v-model="user.phone" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Miasto"
        prop="city"
      >
        <el-input v-model="user.address.city" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Ulica"
        prop="street"
      >
        <el-input v-model="user.address.street" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Kod pocztowy"
        prop="zip_code"
      >
        <el-input v-model="user.address.zip_code" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Państwo"
        prop="country"
      >
        <el-input v-model="user.address.country" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="NIP"
        prop="nip"
      >
        <el-input v-model="user.nip" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Adres email zweryfikowany"
        prop="email_verified"
      >
        <el-checkbox v-model="user.email_verified" autocomplete="off" />
      </el-form-item>
      
      <!-- Avatar Section -->
      <el-form-item label="Avatar">
        <div class="avatar-upload-section">
          <el-upload
            class="avatar-uploader"
            :action="uploadUrl"
            :show-file-list="false"
            :on-success="handleAvatarSuccess"
            :before-upload="beforeAvatarUpload"
            :headers="uploadHeaders"
            :data="{ avatar_type: 'photo', user_id: user.id }"
            :with-credentials="true"
            name="avatar"
          >
            <img v-if="user.avatar_url" :src="user.avatar_url" class="avatar-preview">
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="upload-tips">
            <p>Kliknij aby przesłać avatar</p>
            <p>Obsługiwane formaty: JPG, PNG do 2MB</p>
          </div>
          <el-button v-if="user.avatar_url" size="small" type="danger" @click="removeAvatar">
            Usuń avatar
          </el-button>
        </div>
      </el-form-item>
      
      <!-- Video Avatar Section -->
      <el-form-item label="Video Avatar">
        <div class="video-avatar-section">
          <el-upload
            class="video-uploader"
            :action="videoUploadUrl"
            :show-file-list="false"
            :on-success="handleVideoAvatarSuccess"
            :before-upload="beforeVideoUpload"
            :headers="uploadHeaders"
            :data="{ avatar_type: 'video_url', user_id: user.id }"
            :with-credentials="true"
            name="video_avatar"
          >
            <video v-if="user.video_avatar_url" :src="user.video_avatar_url" class="video-preview" controls></video>
            <i v-else class="el-icon-video-camera video-uploader-icon"></i>
          </el-upload>
          <div class="upload-tips">
            <p>Kliknij aby przesłać video avatar</p>
            <p>Obsługiwane formaty: MP4, WEBM do 10MB</p>
          </div>
          <el-button v-if="user.video_avatar_url" size="small" type="danger" @click="removeVideoAvatar">
            Usuń video avatar
          </el-button>
        </div>
      </el-form-item>
      
      <el-form-item>
        <el-button type="primary" @click="submitForm()">Zapisz</el-button>
      </el-form-item>
    </el-form>

    <!-- Rolki (Reels): saldo, rabaty, historia -->
    <user-reels v-if="canManageReels && $route.query.id" :user-id="String($route.query.id)" />

    <!-- Company Fix Dialog -->
    <el-dialog :visible.sync="showCompanyFixDialog" title="Utwórz firmę" width="450px">
      <div v-loading="fixCompanyLoading" class="company-fix-dialog">
        <div class="warning-section">
          <el-alert
            title="Użytkownik ma rolę firmową ale brak przypisanej firmy"
            type="warning"
            :closable="false"
            show-icon
          />
        </div>
        
        <el-form label-width="120px" label-position="left" style="margin-top: 20px;">
          <el-form-item label="Typ firmy">
            <el-select v-model="selectedCompanyType" placeholder="Wybierz typ firmy" style="width: 100%">
              <el-option
                v-for="item in companyTypes"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <div class="company-preview">
              <p><strong>Zostanie utworzona firma:</strong></p>
              <p>• <strong>Nazwa:</strong> {{ user.name }}</p>
              <p>• <strong>Typ:</strong> {{ getCompanyTypeLabel(selectedCompanyType) }}</p>
              <p class="info-note">Nazwa i typ firmy mogą być zmienione później w ustawieniach.</p>
            </div>
          </el-form-item>
        </el-form>
        
        <div style="text-align:right; margin-top: 20px;">
          <el-button @click="showCompanyFixDialog = false">
            Nie teraz
          </el-button>
          <el-button type="primary" :loading="fixCompanyLoading" @click="createCompanyForUser">
            Utwórz firmę
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import UserResource from '@/api/user';
import { csrf } from '@/api/auth'; // CSRF token handling
import checkPermission from '@/utils/permission';
import UserReels from './components/UserReels';
const userResource = new UserResource();
export default {
  name: 'EditUser',
  components: { UserReels },
  data() {
    return {
      user: {},
      uploadUrl: process.env.VUE_APP_BASE_API + '/admin/upload/avatar',
      videoUploadUrl: process.env.VUE_APP_BASE_API + '/admin/upload/video-avatar',
      showCompanyFixDialog: false,
      fixCompanyLoading: false,
      selectedCompanyType: 'agency',
      companyTypes: [
        { value: 'agency', label: 'Agencja nieruchomości' },
        { value: 'developer', label: 'Deweloper' }
      ],
    };
  },
  mounted() {
    this.getPost();
  },
  methods: {
    async getPost() {
      this.user = await userResource.get(this.$route.query.id);
      this.checkAndOfferCompanyCreation();
    },
    
    checkAndOfferCompanyCreation() {
      // Check if user has company role but no company
      const companyRoles = ['company', 'agency', 'developer'];
      const hasCompanyRole = this.user.roles && this.user.roles.some(role => companyRoles.includes(role));
      const hasCompanyId = this.user.company_id && this.user.company_id !== null;
      
      if (hasCompanyRole && !hasCompanyId) {
        // Show company type selection dialog
        this.showCompanyFixDialog = true;
      }
    },
    
    async createCompanyForUser() {
      try {
        this.fixCompanyLoading = true;
        const response = await userResource.fixCompany(this.user.id, this.selectedCompanyType);
        
        this.$message({
          type: 'success',
          message: `Firma "${this.getCompanyTypeLabel(this.selectedCompanyType)}" została utworzona pomyślnie!`,
          duration: 3000,
        });
        
        // Close dialog and refresh user data
        this.showCompanyFixDialog = false;
        await this.getPost();
      } catch (error) {
        console.error('Error creating company:', error);
        this.$message({
          type: 'error',
          message: 'Błąd podczas tworzenia firmy. Spróbuj ponownie.',
          duration: 5000,
        });
      } finally {
        this.fixCompanyLoading = false;
      }
    },
    
    getCompanyTypeLabel(type) {
      const typeMap = {
        'agency': 'Agencja nieruchomości',
        'developer': 'Deweloper'
      };
      return typeMap[type] || type;
    },
    submitForm() {
      this.$refs.user.validate(async(valid) => {
        if (!valid) {
          this.$notify({
            title: 'Error',
            message: 'Popraw formularz',
            type: 'error',
            duration: 2000,
          });
          return false;
        } else {
          console.log(this.user)
          await userResource.update(this.$route.query.id, this.user);
          await this.$router.push({ path: '/administrator/users' });
        }
      });
    },
    handlePreview(file) {
      this.previewDialogUrl = file.url;
      this.previewDialogVisible = true;
    },
    handleChangeImages(file) {
      this.activePhotoPreview = false;
      this.post.image = file.raw;
    },
    async handleRemove() {
      this.post.image = null;
    },
    
    // Avatar upload methods
    handleAvatarSuccess(res, file) {
      if (res.success) {
        this.user.avatar_url = res.url;
        this.user.avatar = res.path;
        this.$message.success('Avatar został przesłany pomyślnie');
      } else {
        this.$message.error('Błąd podczas przesyłania avatara');
      }
    },
    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error('Avatar musi być w formacie JPG lub PNG!');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('Avatar nie może przekraczać 2MB!');
        return false;
      }

      // Call CSRF before upload
      return csrf().then(() => {
        return true;
      }).catch(err => {
        this.$message.error('Błąd uwierzytelniania');
        return false;
      });
    },
    removeAvatar() {
      this.user.avatar_url = '';
      this.user.avatar = null;
    },
    
    // Video avatar upload methods
    handleVideoAvatarSuccess(res, file) {
      if (res.success) {
        this.user.video_avatar_url = res.url;
        this.user.video_avatar = res.path;
        this.$message.success('Video avatar został przesłany pomyślnie');
      } else {
        this.$message.error('Błąd podczas przesyłania video avatara');
      }
    },
    beforeVideoUpload(file) {
      const isVideo = file.type === 'video/mp4' || file.type === 'video/webm';
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isVideo) {
        this.$message.error('Video avatar musi być w formacie MP4 lub WEBM!');
        return false;
      }
      if (!isLt10M) {
        this.$message.error('Video avatar nie może przekraczać 10MB!');
        return false;
      }

      // Call CSRF before upload
      return csrf().then(() => {
        return true;
      }).catch(err => {
        this.$message.error('Błąd uwierzytelniania');
        return false;
      });
    },
    removeVideoAvatar() {
      this.user.video_avatar_url = '';
      this.user.video_avatar = null;
    },
    
    getCSRFToken() {
      // Get XSRF token from cookie
      const token = document.cookie.split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='));
      return token ? decodeURIComponent(token.split('=')[1]) : '';
    }
  },
  computed: {
    canManageReels() {
      return checkPermission(['manage user']);
    },
    uploadHeaders() {
      return {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': this.getCSRFToken()
      };
    }
  },
};
</script>

<style lang="scss" scoped>
.company-fix-dialog {
  .warning-section {
    margin-bottom: 16px;
  }
  
  .company-preview {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    
    p {
      margin: 4px 0;
      font-size: 14px;
    }
    
    .info-note {
      font-size: 12px;
      color: #6c757d;
      font-style: italic;
      margin-top: 8px;
    }
  }
}
  .add-post {
    margin: 0 30px;
    .add-post-form {
      //width: 50%;
      .post-photo {
        height: 140px;
      }
    }
  }

/* Avatar upload styles */
.avatar-upload-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .avatar-uploader {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s;
    
    &:hover {
      border-color: #409eff;
    }
    
    .avatar-preview {
      width: 120px;
      height: 120px;
      display: block;
      object-fit: cover;
    }
    
    .avatar-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 120px;
      height: 120px;
      line-height: 120px;
      text-align: center;
      display: block;
    }
  }
  
  .upload-tips {
    margin: 8px 0;
    font-size: 12px;
    color: #666;
    
    p {
      margin: 2px 0;
    }
  }
}

/* Video avatar upload styles */
.video-avatar-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .video-uploader {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s;
    
    &:hover {
      border-color: #409eff;
    }
    
    .video-preview {
      width: 200px;
      height: 120px;
      display: block;
      object-fit: cover;
    }
    
    .video-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 200px;
      height: 120px;
      line-height: 120px;
      text-align: center;
      display: block;
    }
  }
  
  .upload-tips {
    margin: 8px 0;
    font-size: 12px;
    color: #666;
    
    p {
      margin: 2px 0;
    }
  }
}
</style>
